import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDrillDownQuery } from '@/hooks/useReportQuery';
import { DrillDownTransactionTable } from './DrillDownTransactionTable';
import type { DrillDownContext } from '@/types/custom-reports';

interface ReportDrillDownModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: DrillDownContext | null;
}

/**
 * Modal dialog for displaying drill-down transaction details.
 * Shows all transactions that contribute to a specific group in the report.
 */
export function ReportDrillDownModal({
  open,
  onOpenChange,
  context,
}: ReportDrillDownModalProps) {
  const { data: transactions, isLoading, error, refetch } = useDrillDownQuery(context);

  // Generate title based on context
  const getModalTitle = () => {
    if (!context) return 'Detalhes das Transações';

    const groupByLabel = getGroupByLabel(context.groupBy);
    return `Transações: ${context.groupLabel} (${groupByLabel})`;
  };

  // Generate description with filter info
  const getModalDescription = () => {
    if (!context) return '';

    const transactionType =
      context.filters.transactionType === 'income' ? 'Receitas' :
      context.filters.transactionType === 'expense' ? 'Despesas' :
      'Todas as transações';

    return `${transactionType} • ${formatDateRange(context.dateRange)}`;
  };

  const handleRetry = () => {
    refetch();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="text-xl pr-8">
                {getModalTitle()}
              </DialogTitle>
              <DialogDescription className="mt-2">
                {getModalDescription()}
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Filter Badges */}
          {context && (
            <div className="flex flex-wrap gap-2 mt-4">
              <Badge variant="secondary">
                {context.groupLabel}
              </Badge>
              {context.filters.status !== 'all' && (
                <Badge variant="outline">
                  {getStatusLabel(context.filters.status)}
                </Badge>
              )}
              {context.filters.categoryIds && context.filters.categoryIds.length > 0 && (
                <Badge variant="outline">
                  {context.filters.categoryIds.length} categorias
                </Badge>
              )}
              {context.filters.costCenterIds && context.filters.costCenterIds.length > 0 && (
                <Badge variant="outline">
                  {context.filters.costCenterIds.length} centros de custo
                </Badge>
              )}
            </div>
          )}
        </DialogHeader>

        {/* Content */}
        <ScrollArea className="max-h-[calc(90vh-200px)]">
          <div className="px-6 py-4">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                  <p className="text-muted-foreground">Carregando transações...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {!isLoading && error && (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-destructive mb-4">
                  Erro ao carregar transações: {(error as Error).message}
                </p>
                <Button variant="outline" onClick={handleRetry}>
                  Tentar Novamente
                </Button>
              </div>
            )}

            {/* Transaction List */}
            {!isLoading && !error && transactions && (
              <>
                {transactions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <p className="text-muted-foreground">
                      Nenhuma transação encontrada para este filtro.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 text-sm text-muted-foreground">
                      {transactions.length} {transactions.length === 1 ? 'transação encontrada' : 'transações encontradas'}
                    </div>
                    <DrillDownTransactionTable transactions={transactions} />
                  </>
                )}
              </>
            )}

            {/* No Context */}
            {!isLoading && !context && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-muted-foreground">
                  Nenhum contexto de drill-down disponível.
                </p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getGroupByLabel(groupBy: string): string {
  switch (groupBy) {
    case 'category':
      return 'Categoria';
    case 'cost_center':
      return 'Centro de Custo';
    case 'client':
      return 'Cliente';
    case 'supplier':
      return 'Fornecedor';
    case 'month':
      return 'Mês';
    case 'week':
      return 'Semana';
    default:
      return 'Grupo';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'paid':
      return 'Pago';
    case 'pending':
      return 'Pendente';
    case 'overdue':
      return 'Atrasado';
    case 'cancelled':
      return 'Cancelado';
    default:
      return status;
  }
}

function formatDateRange(dateRange: { type: string; start?: string; end?: string }): string {
  if (dateRange.type === 'this_month') {
    return 'Este Mês';
  }
  if (dateRange.type === 'last_month') {
    return 'Mês Passado';
  }
  if (dateRange.type === 'today') {
    return 'Hoje';
  }
  if (dateRange.type === 'this_week') {
    return 'Esta Semana';
  }
  if (dateRange.type === 'custom' && dateRange.start && dateRange.end) {
    return `${new Date(dateRange.start).toLocaleDateString('pt-BR')} - ${new Date(dateRange.end).toLocaleDateString('pt-BR')}`;
  }
  return dateRange.type;
}
